import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { UseQueryOptions } from '@tanstack/react-query'
import type {
  ColumnFiltersState,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table'
import { Table } from '../Table/index'
import type { ColumnDefinition, ColumnFilterType, SelectionAction, TableTab } from '../Table/types'
import { useTablePreferences } from '../Table/hooks/useTablePreferences'
import { loadViewPreference, saveViewPreference } from '../Table/tablePreferences'
import { useListQuery } from '../Table/hooks/useListQuery'
import {
  useInfiniteListQuery,
  type CursorPage,
  type CursorPageArgs,
} from '../Table/hooks/useInfiniteListQuery'
import { useDebouncedValue } from '../Table/hooks/useDebouncedValue'
import { apiSortToSorting, tableStateToListParams } from '../Table/listParams'
import { columnFiltersToChipState, chipStateToColumnFilters } from '../Table/columnAdapter'
import { FilterToolbarChip } from '../Table/components/FilterToolbarChip'
import { FiltersConfigButton } from '../Table/components/ViewConfigMenu'
import { BulkSelectionBar } from '../Table/components/BulkSelectionBar'
import { TablePopover } from '../Table/components/TablePopover'
import type { ListParams, ListResult } from '../../api/listApi.types'
import { Map as MapView } from '../Map/index'
import type { FleetSiteMapPoint } from '../Map/index'
import { DeckView } from './DeckView'
import { StackView } from './StackView'
import { LogView } from './LogView'
import { ChartView } from './ChartView'
import { TimelineView } from './TimelineView'
import type { HistoryEntityLink } from './logSummaryLinks'
import type { CardRenderContext, DeckGroupConfig, GroupConfig, ListViewMode, ListViewContextAction, ListViewHeaderAction, RowFlashState, TimelineBarMapping } from './types'
import type { ListViewCTAGroup } from './components/ListViewCard'
import { Button } from '../Button'
import { ListViewToolbarActions } from './ListViewToolbarActions'
import { TabSwitcher } from '../TabSwitcher'
import { FiltersSheetModal } from './FiltersSheetModal'
import { useNarrowChrome } from './useNarrowChrome'
import * as s from './ListView.css'
import { useT } from "../../../app/lib/i18n";
import { useAuth } from "../../../app/lib/auth/hooks";
import { SavedViewsControl } from "./savedViews";
import type { SavedViewSnapshot } from "./savedViews";

export type { ListViewMode, CardRenderContext, DeckGroupConfig, GroupConfig, ListViewContextAction, ListViewHeaderAction, RowFlashState, TimelineBarMapping }
export { DeckView, StackView, LogView, TimelineView }

/** Fullscreen API rejects if the document is not currently fullscreen (e.g. Escape / navigation already exited). */
function exitFullscreenSafe() {
  if (!document.fullscreenElement) return
  void document.exitFullscreen().catch(() => {})
}

// ─── View icons ───────────────────────────────────────────────────────────────

const VIEW_ICONS: Record<ListViewMode, ReactNode> = {
  table: (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18" />
    </svg>
  ),
  deck: (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  stack: (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="5" rx="1" />
      <rect x="3" y="11" width="18" height="5" rx="1" />
      <rect x="3" y="18" width="18" height="2" rx="1" />
    </svg>
  ),
  map: (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
      <line x1="9" y1="3" x2="9" y2="18" />
      <line x1="15" y1="6" x2="15" y2="21" />
    </svg>
  ),
  log: (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 6h13M8 12h13M8 18h13" />
      <path d="M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  ),
  chart: (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 4 3 5-6" />
    </svg>
  ),
  timeline: (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="3" y1="6" x2="14" y2="6" />
      <line x1="3" y1="12" x2="19" y2="12" />
      <line x1="3" y1="18" x2="10" y2="18" />
    </svg>
  ),
}

const DEFAULT_VIEW_LABELS: Record<ListViewMode, string> = {
  table: 'Table',
  deck: 'Deck',
  stack: 'Stack',
  map: 'Map',
  log: 'Log',
  chart: 'Chart',
  timeline: 'Timeline',
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ListViewProps<TRow extends { id: string }> {
  listId: string
  userId?: string
  columns: ColumnDefinition<TRow>[]
  fetchList: (params: ListParams) => Promise<ListResult<TRow>>
  /**
   * Opt-in cursor/infinite ("load more") pagination. When provided, the offset
   * `fetchList` path is disabled and rows accumulate across pages instead of
   * showing numbered pages. Intended for chronological feeds (e.g. history/log).
   */
  cursorFeed?: {
    fetchPage: (args: CursorPageArgs) => Promise<CursorPage<TRow>>
  }

  // ── Views ──────────────────────────────────────────────────────────────
  enabledViews?: ListViewMode[]
  viewLabels?: Partial<Record<ListViewMode, string>>

  // ── Deck ───────────────────────────────────────────────────────────────
  renderCard?: (row: TRow, ctx: CardRenderContext) => ReactNode
  deckGroup?: DeckGroupConfig<TRow>
  deckCardHeight?: number
  /** When set, deck rows use `minmax(Npx, auto)`. Pass `0` for content-sized rows (overrides the default 300px). */
  deckCardMinHeight?: number

  // ── Stack ──────────────────────────────────────────────────────────────
  renderStackCard?: (row: TRow, ctx: CardRenderContext) => ReactNode
  /** Actions available per-row in the ⋯ context menu AND in the bulk selection bar (intersection across selected rows). */
  getContextActions?: (row: TRow) => ListViewContextAction[]

  // ── Map ────────────────────────────────────────────────────────────────
  /** Return `null` to exclude a row from the map (e.g. missing coordinates). */
  toMapPoint?: (row: TRow) => FleetSiteMapPoint | null
  onSiteClick?: (site: FleetSiteMapPoint) => void

  // ── Timeline ───────────────────────────────────────────────────────────
  timelineMapping?: TimelineBarMapping<TRow>
  /** ISO timestamps bounding the visible range — defaults to the active date-range filter, if any. */
  timelineRangeStart?: string
  timelineRangeEnd?: string
  timelineGroup?: GroupConfig<TRow>
  renderTimelineHoverCard?: (
    activeRows: TRow[],
    cursorTimeMs: number,
    hoveredId: string | null,
    selectedId: string | null,
  ) => ReactNode
  /** Controlled selection for the Timeline's clickable bars — see TimelineView. */
  timelineSelectedId?: string | null
  onTimelineBarSelect?: (row: TRow | null) => void
  /** Right-side "alert context" panel content for the selected bar's row. */
  renderTimelineAlertContextPanel?: (row: TRow) => ReactNode

  // ── Shared passthrough (mirrors QueryTableProps) ───────────────────────
  searchPlaceholder?: string
  defaultSort?: { sort_by: string; order: 'asc' | 'desc' }
  defaultPageSize?: number
  enableSelection?: boolean
  /**
   * When true with `enableSelection`, at most one row may be selected.
   * Stack/deck hide checkboxes and use the selected-card outline highlight instead;
   * clicking a card selects it (replacing any prior selection).
   */
  singleSelect?: boolean
  /** When selection is on, return false to disable the checkbox for a row. */
  getRowCanSelect?: (row: TRow) => boolean
  selectionActions?: SelectionAction[]
  getSelectionActions?: (selectedIds: string[], data: TRow[]) => SelectionAction[]
  /** Fires whenever the selection set changes (table / deck / stack). */
  onSelectionChange?: (selectedIds: string[]) => void
  /**
   * Seeds selection when the list mounts or when `contextKey` changes.
   * Useful for modals that want “select all” by default.
   */
  defaultSelectedIds?: string[]
  tabs?: TableTab[]
  fillContainer?: boolean
  emptyTitle?: string
  emptySubtitle?: string
  emptyIcon?: 'battery' | 'users'
  enableRowActions?: boolean
  enableColumnReorder?: boolean
  enableViewConfig?: boolean
  onRowClick?: (row: TRow) => void
  /** Navigate when a linked entity name is clicked in Log view summaries. */
  onLogEntityClick?: (link: HistoryEntityLink) => void
  getRowAccent?: (row: TRow) => string | undefined
  getRowLabel?: (row: TRow) => string
  /** Rows returning true are sorted to the top of all views and highlighted in the table. */
  getRowPinned?: (row: TRow) => boolean
  /** Returns CTA descriptor for a row — shown in card overlays and always-visible in table rows. */
  getRowCtas?: (row: TRow) => ListViewCTAGroup[] | undefined
  disableLocalPersistence?: boolean
  searchDebounceMs?: number
  contextKey?: string
  /**
   * Busts the query cache and forces a refetch when it changes — unlike
   * `contextKey`, does NOT reset filters/selection. Use this for "new data
   * may exist under the hood" signals (e.g. a WebSocket push) where the
   * user's current filter/search state should be left alone.
   */
  refreshToken?: string | number
  initialColumnFilters?: ColumnFiltersState
  footer?: ReactNode
/**
 * Toolbar actions (⋯ menu or expanded buttons, to the right of Saved Views).
 * Multiple actions are listed in array order; when expanded, index 0 is nearest the right.
 */
  headerActions?: ListViewHeaderAction[]
  /**
   * When true (default), render `headerActions` as individual buttons on the toolbar.
   * Collapses into a ⋯ menu when the toolbar is too narrow for filters + actions.
   * Ignored when Saved Views is available — actions always use the ⋯ menu then.
   */
  expandActions?: boolean
  queryOptions?: Omit<UseQueryOptions<ListResult<TRow>>, 'queryKey' | 'queryFn'>
  /** Render cards in a compact/condensed style: smaller image, title + badges only. */
  compact?: boolean
  /**
   * External flash triggers: map of rowId → version counter. When a row's version
   * increments, a shimmer plays even if no list-data fields changed (e.g. image reorder).
   */
  forcedFlashIds?: Map<string, number>
  /**
   * When enabled, adds a Date Range chip to the Filters dropdown.
   * The selected range is passed to fetchList as filters.timeRangeStart / filters.timeRangeEnd (ISO strings).
   */
  timeRangeFilter?: {
    enabled?: boolean
    label?: string
  }
  /**
   * When true, shows a persistent icon-only fullscreen toggle to the right of
   * the toolbar actions.
   * Entering fullscreen fills the viewport with this ListView only.
   */
  allowFullscreen?: boolean
  /**
   * Real-time telemetry rows to merge into the Chart view when the selected
   * date range is live-trailing (end near now). Shape matches historical rows.
   */
  chartLiveRows?: Array<{
    id: string
    time: string
    key: string
    label: string
    raw: string | number | null
    unit?: string
  }>
  /**
   * When true (and `resourceType` is set), shows the Saved Views control above
   * the search/filter toolbar.
   */
  enableSavedViews?: boolean
  /** Entity type for Saved Views (e.g. "users", "organizations", "products"). */
  resourceType?: string
}

// ─── Deck/Map toolbar (search + filter chips + card fields button) ────────────

interface DeckMapToolbarProps<TRow extends { id: string }> {
  search: string
  onSearchChange: (v: string) => void
  searchPlaceholder: string
  tabs?: TableTab[]
  activeTab?: string
  onTabChange?: (v: string) => void
  filterableColumns: ColumnDefinition<TRow>[]
  allFilterColumns: ColumnDefinition<TRow>[]
  visibleFilterIds: string[]
  onVisibleFilterIdsChange: (ids: string[]) => void
  columnFilters: ColumnFiltersState
  rows: TRow[]
  onChipFilterChange: (colId: string, values: string[] | null) => void
  onClearAllFilters: () => void
  hasActiveFilters: boolean
  sortableColumns: ColumnDefinition<TRow>[]
  sort: { sort_by: string; order: 'asc' | 'desc' } | undefined
  onSortChange: (sort: { sort_by: string; order: 'asc' | 'desc' }) => void
  toolbarEnd?: ReactNode
  /** Trailing controls on the filter/search row, right-aligned (e.g. Saved Views). */
  toolbarAbove?: ReactNode
  /** Alternate stacked toolbar for narrow containers. */
  narrowChrome?: boolean
}

function DeckMapToolbar<TRow extends { id: string }>({
  search,
  onSearchChange,
  searchPlaceholder,
  tabs,
  activeTab,
  onTabChange,
  filterableColumns,
  allFilterColumns,
  visibleFilterIds,
  onVisibleFilterIdsChange,
  columnFilters,
  rows,
  onChipFilterChange,
  onClearAllFilters,
  hasActiveFilters,
  sortableColumns,
  sort,
  onSortChange,
  toolbarEnd,
  toolbarAbove,
  narrowChrome = false,
}: DeckMapToolbarProps<TRow>) {
  const t = useT();
  const chipFilters = useMemo(() => columnFiltersToChipState(columnFilters), [columnFilters])
  const [searchFocused, setSearchFocused] = useState(false)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const [sortOpen, setSortOpen] = useState(false)
  const sortBtnRef = React.useRef<HTMLButtonElement>(null)
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false)

  const currentSortCol = sortableColumns.find(
    (c) => (c.sortKey ?? c.id) === sort?.sort_by,
  )
  const currentSortLabel = currentSortCol
    ? (currentSortCol.header ?? (typeof currentSortCol.label === 'string' ? currentSortCol.label : currentSortCol.id))
    : (sort?.sort_by ?? '—')

  if (narrowChrome) {
    return (
      <>
        <div className={s.compactToolbar}>
          {tabs && tabs.length > 0 ? (
            <div className={s.compactToolbarRow}>
              <div className={s.scopeControl} role="tablist">
                {tabs.map(tab => (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={tab.value === activeTab}
                    className={`${s.scopeBtn}${tab.value === activeTab ? ' is-active' : ''}`}
                    onClick={() => onTabChange?.(tab.value)}
                  >
                    {tab.label}
                    <span className={s.scopeCount}>{tab.count}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className={s.compactToolbarRow}>
            <div
              className={`${s.searchWrap} ${s.compactSearchWrap}`}
              data-expanded="true"
            >
              <svg className={s.searchIcon} width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
              </svg>
              <input
                ref={searchInputRef}
                className={s.searchInput}
                placeholder={searchPlaceholder}
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                aria-label={searchPlaceholder}
              />
              {search.trim() ? (
                <button
                  type="button"
                  className={s.searchClearBtn}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onSearchChange('')
                    searchInputRef.current?.focus()
                  }}
                  aria-label={t("shared.filter.clearSearch")}
                  title={t("shared.filter.clearSearch")}
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              ) : null}
            </div>
          </div>

          <div className={s.compactToolbarRow}>
            <button
              type="button"
              className={s.compactFiltersBtn}
              onClick={() => setFiltersSheetOpen(true)}
            >
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M4 6h16M7 12h10M10 18h4" />
              </svg>
              Quick Filters
            </button>
            {hasActiveFilters ? (
              <button
                type="button"
                className={s.compactClearBtn}
                onClick={onClearAllFilters}
                aria-label={t("shared.listView.clearAll")}
                title={t("shared.listView.clearAll")}
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            ) : null}
            <FiltersConfigButton
              filterColumns={allFilterColumns}
              visibleFilterIds={visibleFilterIds}
              onVisibleFilterIdsChange={onVisibleFilterIdsChange}
              presentation="modal"
            />
          </div>

          {(toolbarAbove || toolbarEnd) ? (
            <div className={s.compactToolbarRowBetween}>
              {toolbarAbove}
              {toolbarEnd}
            </div>
          ) : null}
        </div>

        <FiltersSheetModal
          open={filtersSheetOpen}
          onOpenChange={setFiltersSheetOpen}
          allFilterColumns={allFilterColumns}
          visibleFilterIds={visibleFilterIds}
          columnFilters={columnFilters}
          rows={rows}
          onChipFilterChange={onChipFilterChange}
          sortableColumns={sortableColumns}
          sort={sort}
          onSortChange={onSortChange}
          showCounts={false}
        />
      </>
    )
  }

  return (
    <>
      <div className={s.listViewToolbar} data-listview-toolbar="">
      {tabs && tabs.length > 0 && (
        <>
          <div className={s.scopeControl} role="tablist">
            {tabs.map(tab => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={tab.value === activeTab}
                className={`${s.scopeBtn}${tab.value === activeTab ? ' is-active' : ''}`}
                onClick={() => onTabChange?.(tab.value)}
              >
                {tab.label}
                <span className={s.scopeCount}>{tab.count}</span>
              </button>
            ))}
          </div>
          <div className={s.slimToolbarSep} />
        </>
      )}

      <div
        className={s.searchWrap}
        data-expanded={
          searchFocused || search.trim().length > 0 ? 'true' : undefined
        }
        onClick={() => searchInputRef.current?.focus()}
      >
        <svg className={s.searchIcon} width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
        </svg>
        <input
          ref={searchInputRef}
          className={s.searchInput}
          placeholder={searchPlaceholder}
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          aria-label={searchPlaceholder}
        />
        {search.trim() ? (
          <button
            type="button"
            className={s.searchClearBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onSearchChange('')
              searchInputRef.current?.focus()
            }}
            aria-label={t("shared.filter.clearSearch")}
            title={t("shared.filter.clearSearch")}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        ) : null}
      </div>

      {filterableColumns.map(col => (
        <FilterToolbarChip
          key={col.id}
          column={col}
          allRows={rows}
          value={chipFilters[col.id] ?? null}
          onChange={vs => onChipFilterChange(col.id, vs)}
          showCounts={false}
        />
      ))}

      {sortableColumns.length > 0 && (
        <>
          <button
            ref={sortBtnRef}
            type="button"
            className={`${s.sortConfigBtn}${sortOpen ? ' is-active' : ''}`}
            onClick={() => setSortOpen(o => !o)}
            aria-expanded={sortOpen}
            aria-label={`Sort by: ${currentSortLabel}`}
            title={`Sort by: ${currentSortLabel}`}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 9l6-6 6 6M6 15l6 6 6-6" />
            </svg>
            <span>{currentSortLabel}</span>
            {sort && (
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {sort.order === 'desc'
                  ? <path d="M12 5v14M5 12l7 7 7-7" />
                  : <path d="M12 19V5M5 12l7-7 7 7" />}
              </svg>
            )}
          </button>
          {sortOpen && (
            <TablePopover
              anchorRef={sortBtnRef}
              open={sortOpen}
              align="start"
              onClose={() => setSortOpen(false)}
            >
              {sortableColumns.map(col => {
                const colKey = col.sortKey ?? col.id
                const colLabel = col.header ?? (typeof col.label === 'string' ? col.label : col.id)
                const isActive = sort?.sort_by === colKey
                const isDesc = isActive && sort?.order === 'desc'
                return (
                  <div
                    key={col.id}
                    className={s.popoverOption}
                    onClick={() => {
                      onSortChange({
                        sort_by: colKey,
                        order: isActive ? (isDesc ? 'asc' : 'desc') : 'asc',
                      })
                      setSortOpen(false)
                    }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
                  >
                    <span style={{ fontWeight: isActive ? 600 : undefined }}>{colLabel}</span>
                    {isActive && (
                      <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        {isDesc
                          ? <path d="M12 5v14M5 12l7 7 7-7" />
                          : <path d="M12 19V5M5 12l7-7 7 7" />}
                      </svg>
                    )}
                  </div>
                )
              })}
            </TablePopover>
          )}
        </>
      )}

      <FiltersConfigButton
        filterColumns={allFilterColumns}
        visibleFilterIds={visibleFilterIds}
        onVisibleFilterIdsChange={onVisibleFilterIdsChange}
      />

      {hasActiveFilters && (
        <button type="button" className={s.clearBtn} onClick={onClearAllFilters}>
          Clear all
        </button>
      )}

      <div className={s.toolbarSpacer} data-toolbar-spacer="" />

      {toolbarAbove}
      {toolbarEnd}
    </div>
    </>
  )
}

// ─── Deck pagination (floating, prev/next anchored to edges) ─────────────────

function DeckPagination({
  page,
  pageCount,
  pageSize,
  total,
  onPage,
  onPageSize,
  center,
  compact = false,
}: {
  page: number
  pageCount: number
  pageSize: number
  total: number
  onPage: (p: number) => void
  onPageSize: (n: number) => void
  center?: ReactNode
  compact?: boolean
}) {
  const t = useT()
  const pages = useMemo(() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i)
    const out: (number | '…')[] = [0]
    if (page > 2) out.push('…')
    for (let i = Math.max(1, page - 1); i <= Math.min(pageCount - 2, page + 1); i++) out.push(i)
    if (page < pageCount - 3) out.push('…')
    out.push(pageCount - 1)
    return out
  }, [page, pageCount])

  const start = page * pageSize + 1
  const end = Math.min(total, (page + 1) * pageSize)
  const rangeLabel = total === 0 ? '0' : `${start}–${end} of ${total}`

  const pageButtons =
    pageCount > 1 ? (
      <div className={s.pageButtons}>
        <button
          type="button"
          className={s.pageBtn}
          onClick={() => onPage(page - 1)}
          disabled={page === 0}
          aria-label={t("shared.listView.previous")}
        >
          ‹
        </button>
        {pages.map((p, i) =>
          p === '…' ? (
            <button key={`e${i}`} type="button" className={s.pageBtn} disabled>…</button>
          ) : (
            <button
              key={p}
              type="button"
              className={`${s.pageBtn}${p === page ? ' is-active' : ''}`}
              onClick={() => onPage(p)}
            >
              {p + 1}
            </button>
          ),
        )}
        <button
          type="button"
          className={s.pageBtn}
          onClick={() => onPage(page + 1)}
          disabled={page >= pageCount - 1}
          aria-label={t("shared.listView.next")}
        >
          ›
        </button>
      </div>
    ) : null

  if (compact) {
    return (
      <div className={s.deckPaginationCompact}>
        <div className={s.deckPaginationCompactInner}>
          {center ? <div className={s.deckPaginationCompactRow}>{center}</div> : null}
          <div className={s.deckPaginationCompactMeta}>
            <div className={s.deckPaginationInfo} style={{ justifySelf: undefined }}>
              <span>{t("shared.listView.cardsPerPage")}</span>
              <select
                className={s.pageSizeSelect}
                value={pageSize}
                onChange={e => onPageSize(Number(e.target.value))}
                aria-label={t("shared.listView.cardsPerPage")}
              >
                {[10, 25, 50, 100].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span>{rangeLabel}</span>
            </div>
          </div>
          {pageButtons ? (
            <div className={s.deckPaginationCompactPages}>{pageButtons}</div>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className={s.deckPagination}>
      <div className={s.deckPaginationInfo}>
        <span>{t("shared.listView.cardsPerPage")}</span>
        <select
          className={s.pageSizeSelect}
          value={pageSize}
          onChange={e => onPageSize(Number(e.target.value))}
          aria-label={t("shared.listView.cardsPerPage")}
        >
          {[10, 25, 50, 100].map(n => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <span style={{ marginLeft: 8 }}>{rangeLabel}</span>
      </div>
      <div className={s.deckPaginationCenter}>{center}</div>
      <div className={s.deckPaginationEnd}>{pageButtons}</div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

const MAP_PAGE_SIZE = 100
/** Chart needs the full flattened measure×time window in one page (telemetry cap ≈ 200 snapshots). */
const CHART_PAGE_SIZE = 10_000

export function ListView<TRow extends { id: string }>({
  listId,
  userId = '',
  columns,
  fetchList,
  cursorFeed,
  enabledViews = ['table'],
  viewLabels,
  renderCard,
  deckGroup,
  deckCardHeight,
  deckCardMinHeight,
  renderStackCard,
  getContextActions,
  toMapPoint,
  onSiteClick,
  timelineMapping,
  timelineRangeStart,
  timelineRangeEnd,
  timelineGroup,
  renderTimelineHoverCard,
  timelineSelectedId,
  onTimelineBarSelect,
  renderTimelineAlertContextPanel,
  searchPlaceholder = 'Search…',
  defaultSort = { sort_by: 'name', order: 'asc' },
  defaultPageSize = 25,
  enableSelection = false,
  singleSelect = false,
  getRowCanSelect,
  selectionActions,
  getSelectionActions,
  onSelectionChange,
  defaultSelectedIds,
  tabs,
  fillContainer = false,
  emptyTitle,
  emptySubtitle,
  emptyIcon,
  enableRowActions = true,
  enableColumnReorder = true,
  enableViewConfig = true,
  onRowClick,
  onLogEntityClick,
  getRowAccent,
  getRowLabel,
  getRowPinned,
  getRowCtas,
  disableLocalPersistence = false,
  searchDebounceMs = 300,
  contextKey,
  refreshToken,
  initialColumnFilters,
  footer,
  headerActions,
  expandActions = true,
  queryOptions,
  forcedFlashIds,
  compact,
  timeRangeFilter,
  allowFullscreen = false,
  chartLiveRows,
  enableSavedViews = false,
  resourceType,
}: ListViewProps<TRow>) {
  const t = useT();
  const { accessToken } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null)
  const narrowChrome = useNarrowChrome(containerRef)
  const [isFullscreen, setIsFullscreen] = useState(false)
  /** When a saved view is active, skip org-context filter reseeding. */
  const savedViewActiveRef = useRef(false)

  useEffect(() => {
    if (!allowFullscreen) return
    const handler = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current)
    }
    document.addEventListener('fullscreenchange', handler)
    return () => {
      document.removeEventListener('fullscreenchange', handler)
      if (document.fullscreenElement === containerRef.current) {
        exitFullscreenSafe()
      }
    }
  }, [allowFullscreen])

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    if (document.fullscreenElement === el) {
      exitFullscreenSafe()
    } else {
      void el.requestFullscreen().catch(() => {})
    }
  }, [])

  // Only reserve the ⋯ actions column when something can populate it.
  const effectiveEnableRowActions =
    enableRowActions &&
    (Boolean(getContextActions) ||
      Boolean(getRowCtas) ||
      (selectionActions?.length ?? 0) > 0 ||
      Boolean(getSelectionActions));
  // ── Cross-view selection state ────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    if (!defaultSelectedIds?.length) return new Set()
    return singleSelect
      ? new Set([defaultSelectedIds[0]])
      : new Set(defaultSelectedIds)
  })

  // Re-seed when the list identity/context changes (e.g. new import preview).
  useEffect(() => {
    if (!defaultSelectedIds?.length) {
      setSelectedIds(new Set())
      return
    }
    setSelectedIds(
      singleSelect
        ? new Set([defaultSelectedIds[0]])
        : new Set(defaultSelectedIds),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset on context change
  }, [contextKey, listId])

  useEffect(() => {
    onSelectionChange?.([...selectedIds])
  }, [selectedIds, onSelectionChange])

  const rowSelectionForTable = useMemo<RowSelectionState>(() => {
    const rs: RowSelectionState = {}
    selectedIds.forEach(id => { rs[id] = true })
    return rs
  }, [selectedIds])

  const onTableRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
    (updater) => {
      setSelectedIds(prev => {
        const current: RowSelectionState = {}
        prev.forEach(id => { current[id] = true })
        const nextState = typeof updater === 'function' ? updater(current) : updater
        const ids = Object.keys(nextState).filter(id => nextState[id])
        if (singleSelect) {
          const added = ids.find(id => !prev.has(id))
          if (added) return new Set([added])
          return new Set(ids.slice(0, 1))
        }
        return new Set(ids)
      })
    },
    [singleSelect],
  )

  // ── Active view state (persisted) ──────────────────────────────────────
  const [activeView, setActiveViewState] = useState<ListViewMode>(() => {
    if (disableLocalPersistence) return enabledViews[0] ?? 'table'
    try {
      const stored = loadViewPreference(userId, listId) as ListViewMode | null
      if (stored && enabledViews.includes(stored)) return stored
    } catch {}
    return enabledViews[0] ?? 'table'
  })

  const setActiveView = useCallback((view: ListViewMode) => {
    setActiveViewState(view)
    if (!disableLocalPersistence) {
      saveViewPreference(userId, listId, view)
    }
  }, [userId, listId, disableLocalPersistence])

  // ── Preferences & data ────────────────────────────────────────────────
  const {
    prefs,
    orderedColumns,
    filterableColumns,
    allFilterColumns,
    onPaginationChange,
    onColumnFiltersChange,
    onSortChange,
    onColumnVisibilityChange,
    onColumnOrderChange,
    onVisibleFilterIdsChange,
    onColumnSizingChange,
    setSearch,
    resetPageIndex,
  } = useTablePreferences({
    userId,
    tableId: listId,
    columns,
    disablePersistence: disableLocalPersistence,
    defaults: {
      pagination: { pageIndex: 0, pageSize: defaultPageSize },
      columnFilters: initialColumnFilters ?? [],
      sort: defaultSort,
    },
  })

  useEffect(() => {
    if (contextKey == null) return
    if (savedViewActiveRef.current) return
    onColumnFiltersChange(initialColumnFilters ?? [])
  }, [contextKey, initialColumnFilters, onColumnFiltersChange])

  const [search, setSearchLocal] = useState(prefs.search ?? '')
  const debouncedSearch = useDebouncedValue(search, searchDebounceMs)

  const sorting = useMemo(
    () => apiSortToSorting(prefs.sort, columns),
    [prefs.sort, columns],
  )

  // ── Virtual date-range column (injected when timeRangeFilter.enabled) ────
  const virtualDateRangeCol = useMemo<ColumnDefinition<TRow> | null>(() => {
    if (!timeRangeFilter?.enabled) return null
    return {
      id: '__timeRange',
      label: timeRangeFilter.label ?? 'Date Range',
      filterType: 'daterange' as ColumnFilterType,
      showFilterByDefault: false,
      render: () => null,
    }
  }, [timeRangeFilter?.enabled, timeRangeFilter?.label])

  const augmentedAllFilterColumns = useMemo(() => {
    if (!virtualDateRangeCol) return allFilterColumns
    return [...allFilterColumns, virtualDateRangeCol]
  }, [allFilterColumns, virtualDateRangeCol])

  const augmentedFilterableColumns = useMemo(() => {
    if (!virtualDateRangeCol) return filterableColumns
    const isVisible = prefs.visibleFilterIds.includes('__timeRange')
    const isActive = prefs.columnFilters.some(
      f => f.id === '__timeRange' && Array.isArray(f.value) && f.value.some(v => v !== ''),
    )
    if (!isVisible && !isActive) return filterableColumns
    return [...filterableColumns, virtualDateRangeCol]
  }, [filterableColumns, virtualDateRangeCol, prefs.visibleFilterIds, prefs.columnFilters])

  const params = useMemo(() => {
    const base = tableStateToListParams({
      // Map view loads all items (no UI pagination); deck/table use prefs
      pagination: activeView === 'map'
        ? { pageIndex: 0, pageSize: MAP_PAGE_SIZE }
        : activeView === 'chart' || activeView === 'timeline'
        ? { pageIndex: 0, pageSize: CHART_PAGE_SIZE }
        : activeView === 'stack' || activeView === 'log'
        ? { pageIndex: prefs.pagination.pageIndex, pageSize: Math.min(prefs.pagination.pageSize, 100) }
        : prefs.pagination,
      sorting,
      columnFilters: prefs.columnFilters,
      search: debouncedSearch,
      columns,
    })
    if (timeRangeFilter?.enabled) {
      const trFilter = prefs.columnFilters.find(f => f.id === '__timeRange')
      const trValue = Array.isArray(trFilter?.value) ? trFilter.value as string[] : null
      if (trValue) {
        const start = trValue[0] || null
        const end = trValue[1] || null
        base.filters = {
          ...base.filters,
          ...(start ? { timeRangeStart: start } : {}),
          ...(end ? { timeRangeEnd: end } : {}),
        }
      }
    }
    return base
  }, [prefs.pagination, prefs.columnFilters, sorting, debouncedSearch, columns, activeView, timeRangeFilter?.enabled])

  const isFeed = !!cursorFeed
  const externalEnabled =
    typeof queryOptions?.enabled === 'boolean' ? queryOptions.enabled : true
  const queryFn = useCallback((p: ListParams) => fetchList(p), [fetchList])

  // contextKey resets filters/selection (see the effect above); refreshToken
  // only busts the cache. Both, when present, are folded into the query key.
  const cacheKeyParts = [
    ...(contextKey != null ? [contextKey] : []),
    ...(refreshToken != null ? [refreshToken] : []),
  ]

  const offsetQuery = useListQuery<TRow>({
    queryKey: cacheKeyParts.length > 0 ? [listId, 'list', ...cacheKeyParts] : [listId, 'list'],
    params,
    queryFn,
    ...queryOptions,
    enabled: !isFeed && externalEnabled,
  })

  const feedQuery = useInfiniteListQuery<TRow>({
    queryKey: cacheKeyParts.length > 0 ? [listId, 'feed', ...cacheKeyParts] : [listId, 'feed'],
    params,
    fetchPage: cursorFeed?.fetchPage ?? (async () => ({ data: [], nextCursor: null, hasMore: false })),
    enabled: isFeed && externalEnabled,
  })

  const data = isFeed ? feedQuery.data : offsetQuery.data
  const total = isFeed ? feedQuery.data.length : offsetQuery.total
  const error = isFeed ? feedQuery.error : offsetQuery.error

  // ── Map: fetch ALL rows matching current filters, looping the paginated
  // list endpoint. Maps are a spatial overview, not a paged list — showing
  // just the current table page would be a confusing, arbitrary subset.
  const MAP_MAX_PAGES = 20 // safety cap: 20 × MAP_PAGE_SIZE(100) = 2,000 rows
  const mapAllEnabled = activeView === 'map' && !!toMapPoint && !isFeed && externalEnabled

  const mapAllParams = useMemo(
    () => ({ sort_by: params.sort_by, order: params.order, search: params.search, filters: params.filters }),
    [params.sort_by, params.order, params.search, params.filters],
  )

  const mapAllQuery = useQuery({
    queryKey: cacheKeyParts.length > 0
      ? [listId, 'map-all', ...cacheKeyParts, mapAllParams]
      : [listId, 'map-all', mapAllParams],
    queryFn: async () => {
      const collected: TRow[] = []
      let page = 1
      let total = Infinity
      while (collected.length < total && page <= MAP_MAX_PAGES) {
        const res = await queryFn({ ...mapAllParams, page, limit: MAP_PAGE_SIZE })
        collected.push(...res.data)
        total = res.total
        if (res.data.length < MAP_PAGE_SIZE) break
        page += 1
      }
      return { data: collected, total, truncated: collected.length < total }
    },
    enabled: mapAllEnabled,
    placeholderData: (prev: { data: TRow[]; total: number; truncated: boolean } | undefined) => prev,
  })

  // In feed mode there are no numbered pages; keep pageCount at 1.
  const pageCount = isFeed
    ? 1
    : Math.max(1, Math.ceil(total / prefs.pagination.pageSize))
  const loading = isFeed ? feedQuery.isLoading : offsetQuery.isLoading
  // Only show the fetching overlay when params changed and stale data is displayed.
  // Background WS-triggered refetches (same params, isPlaceholderData=false) update
  // silently — no overlay, no spinner, just the shimmer on changed rows.
  const fetching = isFeed
    ? feedQuery.isFetching && !feedQuery.isLoading && !feedQuery.isFetchingNextPage
    : offsetQuery.isFetching && !offsetQuery.isLoading && offsetQuery.isPlaceholderData

  const sortedData = useMemo(() => {
    if (!getRowPinned) return data
    const pinned = data.filter(r => getRowPinned(r))
    const rest = data.filter(r => !getRowPinned(r))
    return [...pinned, ...rest]
  }, [data, getRowPinned])

  // ── Real-time update shimmer ───────────────────────────────────────────
  const [flashingRows, setFlashingRows] = useState<Map<string, RowFlashState>>(() => new Map())
  const prevDataRef = useRef<Map<string, TRow>>(new Map())
  const rowGenRef = useRef<Map<string, number>>(new Map())
  const flashCleanupRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    const prev = prevDataRef.current
    const updates = new Map<string, RowFlashState>()

    for (const row of data) {
      const old = prev.get(row.id)
      if (!old) continue
      const changedFields = new Set<string>()
      for (const key of Object.keys(row) as (keyof TRow)[]) {
        const newVal = (row as Record<string, unknown>)[key as string]
        const oldVal = (old as Record<string, unknown>)[key as string]
        const changed = (newVal !== null && typeof newVal === 'object') || (oldVal !== null && typeof oldVal === 'object')
          ? JSON.stringify(newVal) !== JSON.stringify(oldVal)
          : newVal !== oldVal
        if (changed) changedFields.add(key as string)
      }
      if (changedFields.size > 0) {
        const gen = (rowGenRef.current.get(row.id) ?? 0) + 1
        rowGenRef.current.set(row.id, gen)
        updates.set(row.id, { fields: changedFields, gen })
      }
    }

    // Update prevDataRef snapshot
    prevDataRef.current = new Map(data.map(r => [r.id, r]))

    if (updates.size === 0) return

    setFlashingRows(prev => {
      const next = new Map(prev)
      updates.forEach((v, k) => next.set(k, v))
      return next
    })

    // Clear each flashing row after animation completes (2.25s sweep + buffer)
    updates.forEach((_, id) => {
      const existing = flashCleanupRef.current.get(id)
      if (existing) clearTimeout(existing)
      const timer = setTimeout(() => {
        setFlashingRows(prev => {
          const next = new Map(prev)
          next.delete(id)
          return next
        })
        flashCleanupRef.current.delete(id)
      }, 3500)
      flashCleanupRef.current.set(id, timer)
    })
  }, [data])

  // ── Forced flashes (external triggers, e.g. image reorder) ───────────────
  const prevForcedFlashRef = useRef<Map<string, number>>(new Map())
  useEffect(() => {
    if (!forcedFlashIds || forcedFlashIds.size === 0) return
    const prev = prevForcedFlashRef.current
    const updates = new Map<string, RowFlashState>()

    forcedFlashIds.forEach((version, id) => {
      if ((prev.get(id) ?? 0) < version) {
        const gen = (rowGenRef.current.get(id) ?? 0) + 1
        rowGenRef.current.set(id, gen)
        updates.set(id, { fields: new Set(), gen })
      }
    })

    prevForcedFlashRef.current = new Map(forcedFlashIds)

    if (updates.size === 0) return

    setFlashingRows(prev => {
      const next = new Map(prev)
      updates.forEach((v, k) => next.set(k, v))
      return next
    })

    updates.forEach((_, id) => {
      const existing = flashCleanupRef.current.get(id)
      if (existing) clearTimeout(existing)
      const timer = setTimeout(() => {
        setFlashingRows(prev => {
          const next = new Map(prev)
          next.delete(id)
          return next
        })
        flashCleanupRef.current.delete(id)
      }, 1200)
      flashCleanupRef.current.set(id, timer)
    })
  }, [forcedFlashIds])

  // ── Context actions → bulk bar (union across selected rows, disabled if missing from any) ───
  const contextBulkActions = useMemo<SelectionAction[]>(() => {
    if (!getContextActions || selectedIds.size === 0) return []
    const selected = sortedData.filter(r => selectedIds.has(r.id))
    if (selected.length === 0) return []
    const actionSets = selected.map(row => getContextActions(row))
    // Collect unique action ids in first-seen order across all rows (bulk-eligible only)
    const seenIds = new Set<string>()
    const allActions = actionSets
      .flatMap(set => set)
      .filter(a => a.bulk !== false)
      .filter(a => (seenIds.has(a.id) ? false : (seenIds.add(a.id), true)))
    return allActions.map(action => {
      const bulkSets = actionSets.map(set => set.filter(a => a.bulk !== false))
      const missingFromSomeRow = bulkSets.some(set => !set.find(a => a.id === action.id))
      const explicitlyDisabled = bulkSets.some(set => set.find(a => a.id === action.id)?.disabled ?? false)
      const isDisabled = missingFromSomeRow || explicitlyDisabled
      const disabledTooltip = isDisabled
        ? (bulkSets.flatMap(set => set).find(a => a.id === action.id && a.disabled)?.disabledTooltip
            ?? (missingFromSomeRow ? 'Cannot apply to all selected items' : undefined))
        : undefined
      return {
        icon: action.icon,
        label: action.label,
        danger: action.danger,
        disabled: isDisabled,
        disabledTooltip,
        onClick: (ids: string[]) => action.onClick(ids),
      }
    })
  }, [getContextActions, selectedIds, sortedData])

  // ── Scope tabs state ──────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<string | undefined>(tabs?.[0]?.value)

  // ── Sorting ───────────────────────────────────────────────────────────
  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater
      const first = next[0]
      const current = sorting[0]
      const target = first ?? current
      if (!target) return
      const col = columns.find((c) => c.id === target.id)
      onSortChange({
        sort_by: col?.sortKey ?? target.id,
        order: first ? (first.desc ? 'desc' : 'asc') : 'asc',
      })
    },
    [sorting, onSortChange, columns],
  )

  // ── Search ────────────────────────────────────────────────────────────
  const onGlobalFilterChange = useCallback(
    (value: string) => {
      setSearchLocal(value)
      setSearch(value)
      resetPageIndex()
    },
    [setSearch, resetPageIndex],
  )

  // ── Filter chips ──────────────────────────────────────────────────────
  const onColumnFiltersChangeWrapped: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => { onColumnFiltersChange(updater) },
    [onColumnFiltersChange],
  )

  const onChipFilterChange = useCallback(
    (colId: string, values: string[] | null) => {
      const current = columnFiltersToChipState(prefs.columnFilters)
      const next = { ...current, [colId]: values }
      onColumnFiltersChange(chipStateToColumnFilters(next))
      resetPageIndex()
    },
    [prefs.columnFilters, filterableColumns, onColumnFiltersChange, resetPageIndex],
  )

  const onClearAllFilters = useCallback(() => {
    setSearchLocal('')
    setSearch('')
    onColumnFiltersChange([])
    resetPageIndex()
  }, [setSearch, onColumnFiltersChange, resetPageIndex])

  const applySavedViewSnapshot = useCallback(
    (snapshot: SavedViewSnapshot) => {
      savedViewActiveRef.current = true
      setSearchLocal(snapshot.search)
      setSearch(snapshot.search)
      onColumnFiltersChange(snapshot.columnFilters)
      onVisibleFilterIdsChange(snapshot.visibleFilterIds)
      if (snapshot.sort) onSortChange(snapshot.sort)
      resetPageIndex()
    },
    [
      setSearch,
      onColumnFiltersChange,
      onVisibleFilterIdsChange,
      onSortChange,
      resetPageIndex,
    ],
  )

  const clearSavedViewFilters = useCallback(() => {
    savedViewActiveRef.current = false
    onClearAllFilters()
  }, [onClearAllFilters])

  const savedViewsToolbar =
    enableSavedViews && resourceType && accessToken ? (
      <SavedViewsControl
        listId={listId}
        resourceType={resourceType}
        userId={userId}
        accessToken={accessToken}
        columns={columns}
        columnFilters={prefs.columnFilters}
        search={search}
        sort={prefs.sort ?? null}
        visibleFilterIds={prefs.visibleFilterIds}
        onApplySnapshot={applySavedViewSnapshot}
        onClearFilters={clearSavedViewFilters}
        onActiveViewChange={(id) => {
          savedViewActiveRef.current = id != null
        }}
        presentation={narrowChrome ? 'modal' : 'menu'}
        fillWidth={narrowChrome}
      />
    ) : null

  const toolbarTrailing = (() => {
    const hasContextActions = (headerActions?.length ?? 0) > 0 || allowFullscreen
    if (!savedViewsToolbar && !hasContextActions) return null
    // Saved Views present → always collapse actions into ⋯
    const expand = expandActions && !savedViewsToolbar && !narrowChrome
    const actions = hasContextActions ? (
      <ListViewToolbarActions
        actions={headerActions ?? []}
        expand={expand}
        presentation={narrowChrome ? 'modal' : 'menu'}
        fullscreen={
          allowFullscreen
            ? { isFullscreen, onToggle: toggleFullscreen }
            : undefined
        }
      />
    ) : null

    if (narrowChrome) {
      return (
        <>
          {savedViewsToolbar ? (
            <div className={s.compactTrailingSlot}>{savedViewsToolbar}</div>
          ) : (
            <div className={s.compactTrailingSlot} />
          )}
          {actions ? <div className={s.compactActionsSlot}>{actions}</div> : null}
        </>
      )
    }

    return (
      <div className={s.listViewToolbarTrailing}>
        {savedViewsToolbar}
        {actions}
      </div>
    )
  })()

  const hasActiveFilters = prefs.columnFilters.length > 0 || search.trim().length > 0
  const searchTrimmed = search.trim()
  const baseEmptyTitle = emptyTitle ?? 'No results'
  const resolvedEmptyTitle = searchTrimmed
    ? `${baseEmptyTitle} matching “${searchTrimmed}”`
    : baseEmptyTitle
  const resolvedEmptySubtitle = searchTrimmed ? undefined : emptySubtitle
  const emptyActionLabel = searchTrimmed
    ? 'Clear search'
    : prefs.columnFilters.length > 0
      ? 'Clear filters'
      : null
  const handleEmptyAction = () => {
    if (searchTrimmed) {
      onGlobalFilterChange('')
      return
    }
    onClearAllFilters()
  }

  const columnVisibility = prefs.columnVisibility as VisibilityState

  const isDeckOrMap = activeView === 'deck' || activeView === 'stack' || activeView === 'map' || activeView === 'log' || activeView === 'chart' || activeView === 'timeline'
  const resolvedLabels = { ...DEFAULT_VIEW_LABELS, ...viewLabels }
  const effectiveFill = fillContainer || isFullscreen
  const wrapClass = [
    effectiveFill ? s.listViewWrapFill : s.listViewWrap,
    isFullscreen ? s.listViewWrapFullscreen : null,
  ].filter(Boolean).join(' ')

  const showViewSwitcher = enabledViews.length > 1

  const viewSwitcher = showViewSwitcher ? (
    <TabSwitcher
      tabs={enabledViews.map(view => ({
        value: view,
        label: resolvedLabels[view],
        icon: VIEW_ICONS[view],
      }))}
      value={activeView}
      onChange={(v) => setActiveView(v as ListViewMode)}
      aria-label={t("shared.listView.view")}
      iconsOnly={narrowChrome}
    />
  ) : null

  const showDeckPagination =
    !isFeed &&
    (activeView === 'deck' || activeView === 'stack' || activeView === 'log') &&
    !loading &&
    total > 0

  /** Map/chart (and empty deck/stack/log) still need the switcher in a footer. */
  const showSwitcherOnlyFooter =
    Boolean(viewSwitcher) &&
    !isFeed &&
    activeView !== 'table' &&
    !showDeckPagination &&
    !loading

  return (
    <div ref={containerRef} className={wrapClass}>
      {/* Single top toolbar line (deck/map); table owns its own toolbar */}
      {isDeckOrMap && (
        <DeckMapToolbar
          search={search}
          onSearchChange={onGlobalFilterChange}
          searchPlaceholder={searchPlaceholder}
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          filterableColumns={augmentedFilterableColumns}
          allFilterColumns={augmentedAllFilterColumns}
          visibleFilterIds={prefs.visibleFilterIds}
          onVisibleFilterIdsChange={onVisibleFilterIdsChange}
          columnFilters={prefs.columnFilters}
          rows={data}
          onChipFilterChange={onChipFilterChange}
          onClearAllFilters={onClearAllFilters}
          hasActiveFilters={hasActiveFilters}
          sortableColumns={columns.filter(c => c.sortable)}
          sort={prefs.sort}
          onSortChange={onSortChange}
          toolbarAbove={toolbarTrailing}
          narrowChrome={narrowChrome}
        />
      )}

      {/* Active subview */}
      <div className={effectiveFill ? s.listViewBody : undefined}>
        {error ? (
          <div
            style={{
              padding: 16,
              borderRadius: 8,
              border: '1px solid var(--color-border, #E5E7EB)',
              background: 'var(--color-muted, #F9FAFB)',
              color: '#991B1B',
              fontSize: 14,
            }}
            role="alert"
          >
            {error.message}
          </div>
        ) : (
        <>
        {activeView === 'table' && (
          <Table
            columns={orderedColumns}
            rows={sortedData}
            loading={loading}
            fetching={fetching}
            manualPagination
            manualFiltering
            pageCount={pageCount}
            rowCount={total}
            pagination={prefs.pagination}
            onPaginationChange={onPaginationChange}
            sorting={sorting}
            onSortingChange={onSortingChange}
            columnFilters={prefs.columnFilters}
            onColumnFiltersChange={onColumnFiltersChangeWrapped}
            globalFilter={search}
            onGlobalFilterChange={onGlobalFilterChange}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            columnOrder={prefs.columnOrder}
            onColumnOrderChange={onColumnOrderChange}
            visibleFilterIds={prefs.visibleFilterIds}
            onVisibleFilterIdsChange={onVisibleFilterIdsChange}
            filterableColumns={augmentedFilterableColumns}
            allFilterColumns={augmentedAllFilterColumns}
            enableColumnReorder={enableColumnReorder}
            enableViewConfig={enableViewConfig}
            columnSizing={prefs.columnSizing}
            onColumnSizingChange={onColumnSizingChange}
            searchPlaceholder={searchPlaceholder}
            selectionActions={contextBulkActions.length > 0 ? [...contextBulkActions, ...(selectionActions ?? [])] : selectionActions}
            getSelectionActions={contextBulkActions.length > 0
              ? (ids, d) => [...contextBulkActions, ...(getSelectionActions?.(ids, d) ?? [])]
              : getSelectionActions}
            getRowActions={
              getContextActions
                ? (row) =>
                    getContextActions(row).map((a) => ({
                      icon: a.icon,
                      label: a.label,
                      danger: a.danger,
                      disabled: a.disabled,
                      disabledTooltip: a.disabledTooltip,
                      onClick: (ids) => a.onClick(ids),
                    }))
                : undefined
            }
            enableSelection={enableSelection}
            getRowCanSelect={getRowCanSelect}
            enableMultiRowSelection={!singleSelect}
            rowSelection={rowSelectionForTable}
            onRowSelectionChange={onTableRowSelectionChange}
            enableRowActions={effectiveEnableRowActions}
            tabs={tabs}
            emptyTitle={emptyTitle}
            emptySubtitle={emptySubtitle}
            emptyIcon={emptyIcon}
            fillContainer={effectiveFill}
            toolbarAbove={toolbarTrailing}
            paginationCenter={viewSwitcher}
            narrowChrome={narrowChrome}
            sortableColumns={columns.filter((c) => c.sortable)}
            sort={prefs.sort ?? null}
            onSortChange={onSortChange}
            onRowClick={onRowClick}
            getRowAccent={getRowAccent}
            getRowLabel={getRowLabel}
            getRowHighlighted={getRowPinned}
            getRowCtas={getRowCtas}
            footer={footer}
            selectionResetKey={contextKey}
            flashingRows={flashingRows}
          />
        )}

        {activeView === 'deck' && (
          <div className={s.deckBody}>
            <DeckView
              rows={sortedData}
              loading={loading}
              fetching={fetching}
              renderCard={renderCard!}
              group={deckGroup}
              fillContainer={effectiveFill}
              emptyTitle={resolvedEmptyTitle}
              emptyAction={
                emptyActionLabel ? (
                  <button type="button" className={s.clearBtn} onClick={handleEmptyAction}>
                    {emptyActionLabel}
                  </button>
                ) : null
              }
              onRowClick={onRowClick}
              enableSelection={enableSelection}
              singleSelect={singleSelect}
              selectionActions={selectionActions}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              getRowLabel={getRowLabel}
              cardHeight={deckCardHeight}
              cardMinHeight={deckCardMinHeight}
              getRowCtas={getRowCtas}
              getContextActions={getContextActions}
              flashingRows={flashingRows}
              compact={compact}
            />
            {enableSelection && !singleSelect && selectedIds.size > 0 && (() => {
              const ids = [...selectedIds]
              const baseActions = getSelectionActions ? getSelectionActions(ids, data) : (selectionActions ?? [])
              const deckEffectiveActions = [...contextBulkActions, ...baseActions]
              return deckEffectiveActions?.length ? (
                <BulkSelectionBar
                  selectedIds={ids}
                  actions={deckEffectiveActions}
                  onClear={() => setSelectedIds(new Set())}
                  compact={narrowChrome}
                  getSingleName={
                    getRowLabel
                      ? () => {
                          const r = data.find((row) => row.id === ids[0])
                          return r ? getRowLabel(r) : undefined
                        }
                      : undefined
                  }
                />
              ) : null
            })()}
          </div>
        )}

        {activeView === 'stack' && (
          <div className={s.deckBody}>
            <StackView
              rows={sortedData}
              loading={loading}
              fetching={fetching}
              renderCard={renderStackCard ?? renderCard!}
              fillContainer={effectiveFill}
              emptyTitle={resolvedEmptyTitle}
              emptySubtitle={resolvedEmptySubtitle}
              emptyAction={
                emptyActionLabel ? (
                  <button type="button" className={s.clearBtn} onClick={handleEmptyAction}>
                    {emptyActionLabel}
                  </button>
                ) : null
              }
              onRowClick={onRowClick}
              enableSelection={enableSelection}
              singleSelect={singleSelect}
              selectionActions={selectionActions}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              getRowLabel={getRowLabel}
              getRowCtas={getRowCtas}
              getContextActions={getContextActions}
              flashingRows={flashingRows}
              compact={compact}
            />
            {enableSelection && !singleSelect && selectedIds.size > 0 && (() => {
              const ids = [...selectedIds]
              const baseActions = getSelectionActions ? getSelectionActions(ids, data) : (selectionActions ?? [])
              const stackEffectiveActions = [...contextBulkActions, ...baseActions]
              return stackEffectiveActions?.length ? (
                <BulkSelectionBar
                  selectedIds={ids}
                  actions={stackEffectiveActions}
                  onClear={() => setSelectedIds(new Set())}
                  compact={narrowChrome}
                  getSingleName={
                    getRowLabel
                      ? () => {
                          const r = data.find((row) => row.id === ids[0])
                          return r ? getRowLabel(r) : undefined
                        }
                      : undefined
                  }
                />
              ) : null
            })()}
          </div>
        )}

        {activeView === 'map' && toMapPoint && (() => {
          const rawRows = mapAllQuery.data?.data ?? []
          const points = rawRows
            .map(toMapPoint)
            .filter((p): p is FleetSiteMapPoint => p != null)
          const skipped = rawRows.length - points.length
          const truncated = mapAllQuery.data?.truncated ?? false
          return (
            <div className={s.mapViewWrapper}>
              {(truncated || skipped > 0) && (
                <div className={s.mapInfoBanner}>
                  {truncated &&
                    `Showing first ${points.length.toLocaleString()} of ${(mapAllQuery.data?.total ?? 0).toLocaleString()} matching results. `}
                  {skipped > 0 &&
                    `${skipped} item${skipped > 1 ? 's' : ''} without a location ${skipped > 1 ? 'are' : 'is'} not shown.`}
                </div>
              )}
              <MapView
                sites={points}
                onSiteClick={onSiteClick}
                fillContainer={effectiveFill}
              />
            </div>
          )
        })()}

        {activeView === 'log' && (
          <div className={s.deckBody}>
            <LogView
              rows={sortedData.map((row) => {
                const r = row as TRow & {
                  occurredAt?: string
                  type?: string
                  summary?: string
                  createdAt?: string
                  meta?: Record<string, unknown>
                  users?: Array<{ id?: string; displayName?: string }>
                  organizationId?: string | null
                  source?: string
                }
                const sourceLabel =
                  r.source === 'user-service'
                    ? t('shared.listView.log.source.userService')
                    : r.source === 'product-service'
                      ? t('shared.listView.log.source.productService')
                      : undefined
                return {
                  id: row.id,
                  occurredAt: r.occurredAt ?? r.createdAt ?? '',
                  type: r.type ?? 'audit',
                  summary: r.summary ?? '',
                  meta: r.meta ?? {},
                  users: r.users,
                  organizationId: r.organizationId,
                  ...(sourceLabel ? { sourceLabel } : {}),
                }
              })}
              loading={loading}
              fetching={fetching}
              fillContainer={effectiveFill}
              emptyTitle={resolvedEmptyTitle}
              emptySubtitle={resolvedEmptySubtitle}
              emptyAction={
                emptyActionLabel ? (
                  <button type="button" className={s.clearBtn} onClick={handleEmptyAction}>
                    {emptyActionLabel}
                  </button>
                ) : null
              }
              onEntityClick={onLogEntityClick}
              onRowClick={
                onRowClick
                  ? (logRow) => {
                      const full = sortedData.find((r) => r.id === logRow.id)
                      if (full) onRowClick(full)
                    }
                  : undefined
              }
            />
          </div>
        )}

        {activeView === 'chart' && (
          <div className={s.deckBody}>
            <ChartView
              rows={sortedData.map((row) => {
                const r = row as TRow & {
                  time?: string
                  key?: string
                  label?: string
                  raw?: string | number | null
                  unit?: string
                  createdAt?: string
                }
                return {
                  id: row.id,
                  time: r.time ?? r.createdAt ?? '',
                  key: r.key ?? row.id,
                  label: r.label ?? r.key ?? row.id,
                  raw: r.raw ?? null,
                  unit: r.unit ?? '',
                }
              })}
              loading={loading}
              fetching={fetching}
              fillContainer={effectiveFill}
              emptyTitle={resolvedEmptyTitle}
              emptySubtitle={resolvedEmptySubtitle}
              timeRangeStart={(params.filters?.timeRangeStart as string | undefined) ?? null}
              timeRangeEnd={(params.filters?.timeRangeEnd as string | undefined) ?? null}
              liveRows={chartLiveRows}
            />
          </div>
        )}

        {activeView === 'timeline' && timelineMapping && (() => {
          // The Date Range filter chip (timeRangeFilter) takes priority when active,
          // so users can browse the same range control as the Historical Data chart;
          // the timelineRangeStart/End props are a fallback for callers that don't
          // enable that filter.
          const resolvedRangeStart = (params.filters?.timeRangeStart as string | undefined) ?? timelineRangeStart
          const resolvedRangeEnd = (params.filters?.timeRangeEnd as string | undefined) ?? timelineRangeEnd
          if (!resolvedRangeStart || !resolvedRangeEnd) return null
          return (
            <div className={s.deckBody}>
              <TimelineView
                rows={sortedData}
                loading={loading}
                fillContainer={effectiveFill}
                emptyTitle={resolvedEmptyTitle}
                emptySubtitle={resolvedEmptySubtitle}
                mapping={timelineMapping}
                rangeStart={resolvedRangeStart}
                rangeEnd={resolvedRangeEnd}
                group={timelineGroup}
                renderHoverCard={renderTimelineHoverCard}
                selectedId={timelineSelectedId}
                onBarSelect={onTimelineBarSelect}
                renderAlertContextPanel={renderTimelineAlertContextPanel}
              />
            </div>
          )
        })()}
        </>
        )}
      </div>

      {/* Deck / Stack / Log pagination */}
      {showDeckPagination && (
          <DeckPagination
            page={prefs.pagination.pageIndex}
            pageCount={pageCount}
            pageSize={prefs.pagination.pageSize}
            total={total}
            onPage={p => onPaginationChange(prev => ({ ...prev, pageIndex: p }))}
            onPageSize={n => onPaginationChange({ pageIndex: 0, pageSize: n })}
            center={viewSwitcher}
            compact={narrowChrome}
          />
        )}

      {showSwitcherOnlyFooter && (
        <div className={s.listViewFooterSwitcher}>{viewSwitcher}</div>
      )}

      {/* Cursor feed "load more" footer */}
      {isFeed && !loading && total > 0 && (
        <FeedLoadMore
          count={total}
          hasMore={feedQuery.hasNextPage}
          loading={feedQuery.isFetchingNextPage}
          onLoadMore={feedQuery.fetchNextPage}
          countLabel={t('shared.listView.feed.showing', { count: total })}
          loadMoreLabel={t('shared.listView.feed.loadMore')}
          endLabel={t('shared.listView.feed.end')}
          loadingLabel={t('shared.listView.feed.loading')}
        />
      )}
    </div>
  )
}

// ─── Cursor feed footer (auto-load on scroll + manual button) ─────────────────

function FeedLoadMore({
  count,
  hasMore,
  loading,
  onLoadMore,
  countLabel,
  loadMoreLabel,
  endLabel,
  loadingLabel,
}: {
  count: number
  hasMore: boolean
  loading: boolean
  onLoadMore: () => void
  countLabel: string
  loadMoreLabel: string
  endLabel: string
  loadingLabel: string
}) {
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasMore || loading) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore()
      },
      { rootMargin: '200px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, loading, onLoadMore])

  return (
    <div className={s.feedFooter}>
      <span className={s.feedCount}>{countLabel}</span>
      {hasMore ? (
        <>
          <button
            type="button"
            className={s.feedLoadMoreBtn}
            onClick={onLoadMore}
            disabled={loading}
          >
            {loading ? loadingLabel : loadMoreLabel}
          </button>
          <div ref={sentinelRef} aria-hidden className={s.feedSentinel} />
        </>
      ) : (
        <span className={s.feedEnd}>{endLabel}</span>
      )}
    </div>
  )
}
